import { XRayAnalyticsType } from '@mm/state/controllers/BotAnalyticsController';
import { WebsiteTrafficSources } from '@mm/state/controllers/SEMRushWebsiteTrafficController';
import { ObjectUtils } from '@mm/utils';
import { snakeCase } from 'lodash';
import moment from 'moment';
import * as queryString from 'query-string';

const BASE_URL = API_URL;

const getAnalyticsDateQuery = (
  startDate?: string,
  endDate?: string,
  format?: string,
  keywordsQuery?: boolean,
) =>
  startDate && endDate
    ? `${keywordsQuery ? '?' : '&'}start_date=${startDate}&end_date=${endDate}`
    : startDate && !endDate
    ? `${keywordsQuery ? '?' : '&'}start_date=${startDate}&end_date=${moment(
        new Date(),
      ).format(format || 'YYYY-MM-DD')}`
    : '';

// Node Live Chat URLs:
export const LC_CONVERSATIONS = `${LIVE_CHAT_API_URL}conversations`;
export const LC_CONVERSATIONS_SEARCH = `${LC_CONVERSATIONS}/search`;
export const LC_CONVERSATION_SESSION_MESSAGES = `${LC_CONVERSATIONS}/conversation_session_messages`;
export const LC_MESSAGES = `${LC_CONVERSATIONS}/messages`;
export const BATCH_LC_MESSAGES = `${LC_CONVERSATIONS}/batch_messages`;
export const LC_SELECTED_REMOTE_USER = `${LC_CONVERSATIONS}/remote_user`;
export const LC_CONTACTS = `${LIVE_CHAT_API_URL}contacts`;
export const LC_CONTACTS_SEARCH = `${LC_CONTACTS}/search`;
export const LC_SCREENSHOT = (url: string, width: number, height: number) =>
  `${LIVE_CHAT_API_URL}screenshot/image.png?${queryString.stringify({
    url,
    width,
    height,
  })}`;
export const LC_SCREENSHOT_FB_WIDGET = `${LIVE_CHAT_API_URL}screenshot/facebook_chat_widget.png`;

export const LC_GLOBAL_JS_TROUBLESHOOT = (
  url: string,
  remotePageId: string,
  chatWidgetChannel: string,
) =>
  `${LIVE_CHAT_API_URL}global_js/troubleshoot?${queryString.stringify({
    url,
    remote_page_id: remotePageId,
    chat_widget_channel: chatWidgetChannel,
  })}`;

// Rails API:
export const LOGIN = `${BASE_URL}session`;
export const SESSION_FACEBOOK = `${BASE_URL}session/facebook`;
export const SESSION_SEMRUSH = `${BASE_URL}session/semrush`;
export const SESSION_LOGOUT = `${BASE_URL}session/logout`;
export const SESSION_COMPANY = `${BASE_URL}session/company`;
export const VERIFY_EMAIL_REQUEST = `${BASE_URL}verify_email`;
export const VERIFY_EMAIL = (token: string) =>
  `${BASE_URL}verify_email/${token}`;
export const RESET_PASSWORD = `${BASE_URL}reset_password`;
export const RESET_PASSWORD_CONFIRM = `${BASE_URL}reset_password/confirm`;
export const USERS = `${BASE_URL}users`;
export const SIGNUP = `${BASE_URL}signup`;
export const BOTS = `${BASE_URL}bots`;
export const ACTIVATE_BOT = `${BASE_URL}bots/activate`;
export const SEND_CAMPAIGNS = `${BASE_URL}campaigns`;
export const CAMPAIGN_PURPOSES = `${BASE_URL}campaigns/purposes`;
export const UPDATE_CAMPAIGN = campaignId => `${SEND_CAMPAIGNS}/${campaignId}`;
export const BOT = botId => `${BOTS}/${botId}`;
export const SAMPLE_BOTS = `${BOTS}/sample_bots`;
export const COMPANIES = `${BASE_URL}companies`;
export const COMPANY = id => `${COMPANIES}/${id}`;
export const FACEBOOK_SESSION = `${BASE_URL}session/facebook`;
export const PAGE_GROUPS = `${BASE_URL}page_groups`;
export const PAGES = `${BASE_URL}pages`;
export const PAGE = pageId => `${BASE_URL}pages/${pageId}`;
export const BATCH_PAGES = `${BASE_URL}pages/batch_pages`;
export const LANDING_PAGES = botId => `${BOTS}/${botId}/landing_pages`;
export const LANDING_PAGE_URLS = botId => `${BOT(botId)}/landing_page_urls`;
export const VISITED_URLS = botId => `${BOT(botId)}/visited_urls`;
export const LANDING_PAGE_LOGO = botId =>
  `${LANDING_PAGES(botId)}/logo_upload_link`;
export const SCAN_CODES = botId => `${BOTS}/${botId}/scan_codes`;
export const WIDGETS = `${BASE_URL}widgets`;
export const CTM_INSIGHTS = (botId: number, q: string) =>
  `${BOTS}/${botId}/ctm_insights/insights?${q}`;
export const FOLLOW_UPS = (botId: number, q: string) =>
  `${BOTS}/${botId}/follow_ups?${q}`;
export const FOLLOW_UP = (botId: number, id: number) =>
  `${BOTS}/${botId}/follow_ups/${id}`;

export const ASSETS = `${BASE_URL}assets`;
export const ASSETS_UPLOADED = assetId => `${ASSETS}/${assetId}/uploaded`;
export const UPLOAD_IMAGE = `${ASSETS}/image_upload_link`;
export const UPLOAD_VIDEO = `${ASSETS}/video_upload_link`;
export const UPLOAD_AUDIO = `${ASSETS}/audio_upload_link`;
export const UPLOAD_FILE = `${ASSETS}/attachment_upload_link`;
export const UPLOAD_EMAIL_ATTACHMENT = `${ASSETS}/email_attachment_upload_link`;
export const TRIGGER = `${BASE_URL}triggers`;
export const CONTACTS = `${BASE_URL}contacts`;
export const Analytics = `${BASE_URL}analytics`;
export const WhatsNew = `${BASE_URL}app_news_item`;
export const VALIDATION_MESSAGES = `${BASE_URL}validation_messages`;
export const VALIDATION_MESSAGE = id => `${VALIDATION_MESSAGES}/${id}`;
const ADS = `${BASE_URL}marketing`;
export const AD_AUDIENCES = `${ADS}/audiences`;
export const AD_SETS = (q = '') => `${ADS}/sets${q}`;
export const FULL_AD_SETS = (q = '') => `${ADS}/sets/all_available${q}`;
export const FULL_ADS_SYNC = (q: string = '') =>
  `${ADS}/remote_ads_for_sync${q}`;
export const FULL_ADS = (q: string = '') => `${ADS}/remote_ad_entities${q}`;
export const SPONSORED_MESSAGES = (botId: number) =>
  `${BOTS}/${botId}/sponsored_messages`;
export const SPONSORED_MESSAGES_REPLACEMENT_PAGE = (
  botId: number,
  id: number,
) => `${SPONSORED_MESSAGES(botId)}/${id}/prepare_replacement_page`;
export const MESSAGES_AD_CAMPAIGNS = (q = '') => `${ADS}/campaigns${q}`;
export const CTM_ADS = (botId: number) => `${BOTS}/${botId}/ctm_ads`;
export const ICE_BREAKERS = (botId: number, kind?: 'facebook' | 'instagram') =>
  `${BOTS}/${botId}/ice_breakers${kind ? `?kind=${kind}` : ''}`;
export const ICE_BREAKER = (botId: number, id: number) =>
  `${ICE_BREAKERS(botId)}/${id}`;
export const AD_CONFIGS = (botId: number) => `${BOTS}/${botId}/ad_configs`;
export const AD_CONFIGS_SEARCH = (
  query: string,
  specialCategory: boolean,
  entity?: string,
) => {
  const params = {
    query,
    entity,
    ...(specialCategory ? { specialCategory } : {}),
  };
  const q = queryString.stringify(ObjectUtils.mapObjKeysToSnakeCase(params));
  return `${BASE_URL}search_locations?${q}`;
};
export const MESSENGER_AD_REPLACEMENT_PAGE = (botId: number, id: number) =>
  `${MESSENGER_ADS(botId)}/${id}/prepare_replacement_page`;
export const AD_CONFIGS_REPLACEMENT_PAGE = (botId: number, id: number) =>
  `${AD_CONFIG(botId, id)}/prepare_replacement_page`;
export const AD_CONFIG = (botId: number, id: number) =>
  `${AD_CONFIGS(botId)}/${id}`;
export const PUBLISH_AD_CONFIG = (botId: number, id: number) =>
  `${AD_CONFIG(botId, id)}/publish`;
export const MESSENGER_AD_TEMPLATES = (botId: number) =>
  `${BOTS}/${botId}/ad_configs/ad_templates`;
export const CTM_AD = (botId: number, id: number) => `${CTM_ADS(botId)}/${id}`;

export const AUTOMATION_STATS = `${BASE_URL}automations/statistics`;
export const AUTOMATION_VERIFIED_STATS = (id: number) =>
  `${BASE_URL}campaigns/${id}/verified_stats`;
export const AUTOMATION_TRIGGERS = `${BASE_URL}automations/create_input_triggers`;
export const AUTOMATION_DELETE = (id: number) => `${BASE_URL}automations/${id}`;
export const AUTOMATION_EXISTING_TRIGGERS = `${BASE_URL}automations/existed_input_triggers`;

export const REPORTS = `${BASE_URL}reports`;
export const REPORT = (id: number) => `${BASE_URL}reports/${id}`;

export const EMAIL_ACCOUNTS_TEST = `${BASE_URL}email_accounts/send_test`;

export const COMPLETIONS = fbPageId => `${BASE_URL}completions/${fbPageId}`;

export const AD_AUDIENCE = remoteId => `${AD_AUDIENCES}/${remoteId}`;

export const AD_AUDIENCE_SYNC = remoteId =>
  `${AD_AUDIENCES}/${remoteId}/synchronize`;

export const AD_INSIGHTS = fbPageId => `${ADS}/${fbPageId}/insights`;

export const REMOTE_AD_ACCOUNT = (adAccountId: string) =>
  `${ADS}/remote_ad_account?ad_account_id=${adAccountId}`;

export const REMOTE_AD_ACCOUNTS = (
  fbPageId: number | undefined,
  extended: boolean = false,
  all: boolean = false,
  next: string = '',
) => {
  const baseUri = `${ADS}/remote_ad_accounts?next=${next}${
    extended ? '&extended=true' : ''
  }${all ? '&all=true' : ''}`;
  return fbPageId === undefined
    ? baseUri
    : `${baseUri}&facebook_page_id=${fbPageId}`;
};

export const FB_PAGE_AD_ACCOUNTS = fbPageId => `${ADS}/${fbPageId}/ad_accounts`;
export const ASSIGN_AD_ACCOUNT_TO_FB_PAGE = `${ADS}/assign_ad_account`;
const FB_ADS_COUPONS = `${BASE_URL}fb_ads_coupons`;
export const GET_FB_VACANT_ADS_COUPONS = `${FB_ADS_COUPONS}/vacant_coupons`;
export const FB_ATTACH_VACANT_AD_COUPON = `${FB_ADS_COUPONS}/attach`;

export const CONNECTED_PAGES = (queryString = '') =>
  `${BASE_URL}facebook_pages${queryString}`;
export const CONNECTED_PAGE = (pageId, queryString = '') =>
  `${BASE_URL}facebook_pages/${pageId}${queryString}`;

export const FB_PAGE_CONTACTS = pageId =>
  `${BASE_URL}facebook_pages/${pageId}/contacts`;

export const FB_PAGE_CONTACTS_REMOTE_ID = (pageId, remoteId) =>
  `${BASE_URL}facebook_pages/${pageId}/contacts/by_remote_id/${remoteId}`;

export const ATTACH_BOT_TO_FACEBOOK_PAGE = facebookPageId =>
  `${BASE_URL}facebook_pages/${facebookPageId}/activate_deactivate_bot`;

export const FB_PAGE_CONTACT_CSV = facebookPageId =>
  `${BASE_URL}facebook_pages/${facebookPageId}/contacts/csv`;

export const IMPORT_CONTACTS_CSV_JOB = `${BASE_URL}contacts_csvs`;

export const FB_PAGE_CONTACT_EXPORT = facebookPageId =>
  `${BASE_URL}facebook_pages/${facebookPageId}/contacts/scheduled_exports`;

export const ACCESSIBLE_CONNECTED_PAGES = `${BASE_URL}facebook_pages/accessible_connected_pages`;

export const ACTIVATED_CRMS = facebookPageId =>
  `${BASE_URL}facebook_pages/${facebookPageId}/crms`;

export const DOMAINS = pageId => `${CONNECTED_PAGE(pageId)}/domains`;
export const FACEBOOK_ERRORS = pageId =>
  `${CONNECTED_PAGE(pageId)}/facebook_errors`;
export const FACEBOOK_ERRORS_BASE = (pageId, fbErrorId) =>
  `${BASE_URL}facebook_errors/${fbErrorId}?facebook_page_id=${pageId}`;
export const CONNECTIONS = (
  facebookPageId,
  pageNumber = 1,
  pageSize = 25,
  query,
  type,
) => {
  const queryOptions = query ? `query=${query}&` : '';
  const typeOptions = type ? `type=${type}&` : '';
  const queryString = `?${queryOptions}${typeOptions}page=${pageNumber}&per_page=${pageSize}`;
  return `${BASE_URL}facebook_pages/${facebookPageId}/all_connections${queryString}`;
};
export const AVAILABLE_PAGES = `${BASE_URL}facebook_pages/available_options`;
export const FB_PAGE_CUSTOM_SQUARE_PICTURE = (facebookPageId: number) =>
  `${CONNECTED_PAGE(facebookPageId)}/custom_square_picture`;
export const ENV = `${BASE_URL}env`;
export const SUGGESTIONS = botId => `${BOTS}/${botId}/response_misses`;

export const IGNORE_SUGGESTION = botId =>
  `${BOTS}/${botId}/ignore_response_misses`;

export const ATTRIBUTES = botId => `${BASE_URL}variables/${botId}`;

export const CREATE_ATTRIBUTE = facebookPageId =>
  `${BASE_URL}facebook_pages/${facebookPageId}/variables`;

export const DELETE_ATTRIBUTE = attributeId =>
  `${BASE_URL}variables/${attributeId}`;

export const DELETE_FB_VARIABLE_VALUE = (attributeId: number) =>
  `${BASE_URL}variable_values/${attributeId}`;

export const FACEBOOK_PAGE_VARIABLES = pageId =>
  `${BASE_URL}facebook_pages/${pageId}/variables`;

export const FACEBOOK_PAGE_BUSINESS_VARIABLES = pageId =>
  `${BASE_URL}facebook_pages/${pageId}/variables/business`;

export const BUTTONS = widgetID => `${WIDGETS}/${widgetID}/buttons`;
export const ICE_BREAKER_BUTTONS = (botId: number, iceBreakerId: number) =>
  `${ICE_BREAKER(botId, iceBreakerId)}/buttons`;
export const ICE_BREAKER_BUTTON = (
  botId: number,
  iceBreakerId: number,
  id: number,
) => `${ICE_BREAKER_BUTTONS(botId, iceBreakerId)}/${id}`;
export const CREATE_BUTTON_BUNDLE = (widgetId: number) =>
  `${BUTTONS(widgetId)}/create_bundle`;
export const DELETE_BUTTON_BUNDLE = (widgetId: number, buttonId: number) =>
  `${BUTTONS(widgetId)}/${buttonId}/destroy_bundle`;
export const ENTRIES = widgetID => `${WIDGETS}/${widgetID}/entries`;
export const ENTRY_BUTTONS = widgetId => entryId =>
  `${ENTRIES(widgetId)}/${entryId}/buttons`;
export const CLONE_BOT = `${BOTS}/clone`;
export const TOGGLE_PLATFORM_CHANNELS = (id: number) =>
  `${BOTS}/${id}/toggle_platform_channels`;
export const LANGUAGES = `${BOTS}/languages`;
export const ADMIN = `${BASE_URL.slice(0, -4)}admin`;
export const PROMOTERS = botId => `${BOTS}/${botId}/promoters`;
export const PROMOTER_CHAT_WIDGET_DIAGNOSTIC_LOG = (
  botId: number,
  promoterId: number,
) => `${PROMOTERS(botId)}/${promoterId}/chat_widget_diagnostic_log`;
export const PROMOTER_CHAT_WIDGET_DIAGNOSTIC_RUN_TESTS = (
  botId: number,
  promoterId: number,
) => `${PROMOTERS(botId)}/${promoterId}/chat_widget_diagnostic_run_tests`;
export const NOTIFICATIONS = (botId: number) =>
  `${BOTS}/${botId}/notifications`;
export const WEBHOOK = (facebookPageId, webhookId) =>
  `${BASE_URL}facebook_pages/${facebookPageId}/webhooks/${webhookId}`;
export const WEBHOOK_OAUTH_URL = (facebookPageId: number, webhookId: number) =>
  `${BASE_URL}facebook_pages/${facebookPageId}/webhooks/${webhookId}/oauth_url`;
export const WEBHOOKS_OAUTH_REVOKE_URL = () =>
  `${BASE_URL}/webhooks/oauth/revoke`;
export const WEBHOOKS = facebookPageId =>
  `${BASE_URL}facebook_pages/${facebookPageId}/webhooks`;
export const MAIN_MENU = botId => `${BOTS}/${botId}/main_menu_items`;
export const CAMPAIGNS = (
  botId,
  page,
  perPage,
  additionalParams,
  dripCampaign,
  withSchedulersAttributes,
) => {
  const params = {
    botId,
    page,
    perPage,
    withSchedulersAttributes: Boolean(withSchedulersAttributes),
    scheduleType: dripCampaign ? 'drip' : undefined,
    ...additionalParams,
  };
  const q = queryString.stringify(ObjectUtils.mapObjKeysToSnakeCase(params), {
    arrayFormat: 'bracket',
  });
  return `${BASE_URL}campaigns?${q}`;
};
export const CAMPAIGN_BY_ID = id => `${BASE_URL}campaigns/${id}`;
export const CAMPAIGN_SEARCH_TOKEN_BY_ID = id =>
  `${BASE_URL}campaigns/${id}/search_token`;
export const CAMPAIGN_QUEUED_CONTACTS = (id, schedulerId, page, perPage) => {
  const options = `?page=${page}&per_page=${perPage}`;
  return `${BASE_URL}campaigns/${id}/queued_contacts/${schedulerId}${options}`;
};
export const ERROR_EMAIL = `${BASE_URL}error_email`;
export const ONBOARDING = `${BASE_URL}onboarding`;
export const MESSENGER_ADS = botId => `${BOTS}/${botId}/messenger_ads`;
export const MESSENGER_AD_CREATE_AUDIENCE = (botId: number, adId: number) =>
  `${MESSENGER_ADS(botId)}/${adId}/create_audience`;
export const MESSENGER_AD_DESTROY_AUDIENCE = (botId: number, adId: number) =>
  `${MESSENGER_ADS(botId)}/${adId}/destroy_audience`;
export const MESSENGER_AD_CHANGE_AUDIENCE_NAME = (
  botId: number,
  adId: number,
) => `${MESSENGER_ADS(botId)}/${adId}/change_audience_name`;
export const RSS_FEEDS = botId => `${BOTS}/${botId}/rss_feeds`;
export const USERS_BY_COMPANY = (companyId, pageNumber, pageSize) =>
  `${BASE_URL}users?company_id=${companyId}&page=${pageNumber}&per_page=${pageSize}`;
export const USERS_MY = `${BASE_URL}users/my_user`;
export const USERS_BY_FB_PAGE = (fbPageId, companyId, pageNumber, pageSize) =>
  `${BASE_URL}users?facebook_page_id=${fbPageId}&company_id=${companyId}&page=${pageNumber}&per_page=${pageSize}`;
export const USER_BY_COMPANY = (companyId, userId) =>
  `${BASE_URL}users/${userId}?company_id=${companyId}`;
export const USER_BY_FB_PAGE = (fbPageId, userId) =>
  `${BASE_URL}users/${userId}?facebook_page_id=${fbPageId}`;
export const USER_ATTRIBUTES = userId =>
  `${BASE_URL}users/${userId}/user_attributes`;
export const USER_PAP_AFFILIATE = userId =>
  `${BASE_URL}users/${userId}/pap_affiliate`;
export const AMBASSADOR_INFO = `${BASE_URL}ambassador_info`;
export const REQUEST_DEMO = `${BASE_URL}users/request_demo`;
export const INVITES_BY_ID = inviteId => `${BASE_URL}invites/${inviteId}`;
export const USERS_INVITES = (
  companyId: number,
  page?: number,
  perPage?: number,
  scope?: string,
) => {
  const query = queryString.stringify(
    ObjectUtils.mapObjKeysToSnakeCase({
      page,
      perPage,
      scope,
    }),
  );
  return `${BASE_URL}companies/${companyId}/invites?${query}`;
};

export const AVAILABLE_SEATS = (companyId: number) =>
  `${BASE_URL}companies/${companyId}/invites/available_fb_page_ids`;

export const FBPosts = (pageId: number) => `${CONNECTED_PAGE(pageId)}/posts`;
export const ACCEPT_INVITE_COMPANY = (inviteToken: string) =>
  `${BASE_URL}invite/${inviteToken}`;
export const VALIDATE_INVITE_TOKEN = (inviteToken: string) =>
  `${BASE_URL}invite_check/${inviteToken}`;
export const AUDIENCES = (
  botId,
  pageNumber,
  pageSize,
  query,
  withoutSystemFilters,
) => {
  const queryOptions = query ? `&query=${query}` : '';
  const pageOptions = pageNumber
    ? `?page=${pageNumber}&per_page=${pageSize}`
    : '';
  const withoutSystemFiltersParam = withoutSystemFilters
    ? `&without_system=${withoutSystemFilters}`
    : '';
  return `${BOTS}/${botId}/audiences${pageOptions}${queryOptions}${withoutSystemFiltersParam}`;
};
export const AUDIENCE_CONTACTS = (
  botId,
  audienceId,
  pageNumber,
  pageSize,
  facebookPageId,
  query,
  withDeliverable,
) => {
  const facebookPageQuery = `?facebook_page_id=${facebookPageId}`;
  const pageOptions = pageNumber
    ? `${facebookPageQuery}&page=${pageNumber}&per_page=${pageSize}`
    : facebookPageQuery;
  const queryOptions = query ? `&query=${query}` : '';
  const withDeliverableOption = withDeliverable
    ? `&with_deliverable=${withDeliverable}`
    : '';
  return `${BOTS}/${botId}/audiences/${audienceId}/contacts${pageOptions}${queryOptions}${withDeliverableOption}`;
};
export const GIPHY_PREDICTIONS = (phrase: string) =>
  `//api.giphy.com/v1/gifs/search?q=${phrase}&api_key=kRW0U1M9bEjtZC7wine37cKpJY1ztQXj&rating=g`;
export const DISCONNECT_PROMO = (id: number) =>
  `${BASE_URL}promotions/${id}/disconnect`;
export const CONNECT_PROMO = (id: number) =>
  `${BASE_URL}promotions/${id}/connect`;
export const ATTACH_INSTACHAMP_PROMO = (companyId: number) =>
  `${BASE_URL}companies/${companyId}/promotions/apply_instachamp_code`;
export const ADD_PROMO_TO_USER = (companyId: number) =>
  `${BASE_URL}companies/${companyId}/promotions/add_promo_to_user`;
export const PROMOTIONS_LIST = (companyId: number) =>
  `${BASE_URL}companies/${companyId}/promotions/list`;
export const FOLLOW_UP_CONVERSATIONS = (companyId: number) =>
  `${BASE_URL}companies/${companyId}/follow_up_number`;

export const USER_FIELDS = id => `${USERS}/${id}`;
export const DROPDOWN_PAGES = `${BASE_URL}pages/available_options`;
export const TEST_WEBHOOK = (facebookPageId: number, connectionId: number) =>
  `${BASE_URL}facebook_pages/${facebookPageId}/webhooks/${connectionId}/test`;
export const WIDGET_CONNECTIONS = (facebookPageId: number, type?: string) => {
  const params = type ? `?type=${type}` : '';
  return `${BASE_URL}facebook_pages/${facebookPageId}/widget_webhooks${params}`;
};
export const API_KEYS = (facebookPageId: number) =>
  `${BASE_URL}facebook_pages/${facebookPageId}/api_keys`;
export const API_KEY = (facebookPageId: number, apiKeyId: number) =>
  `${BASE_URL}facebook_pages/${facebookPageId}/api_keys/${apiKeyId}`;
export const COMPANY_FB_PAGES = companyId =>
  `${COMPANIES}/${companyId}/facebook_pages`;

export const PARTNERS = `${BASE_URL}partners`;
export const BROADCAST = `${BASE_URL}broadcasts`;
export const ALLOW_PAGE_FOR_USERS = facebookPageId =>
  `${BASE_URL}facebook_pages/${facebookPageId}/grant_for_users`;
export const MOVE_PAGE_TO_COMPANY = pageId =>
  `${BASE_URL}facebook_pages/${pageId}/move_to_company`;
export const GET_CAMPAIGN_TRACKING = (campaignId, pageNumber, pageSize) => {
  const options = `?page=${pageNumber}&per_page=${pageSize}`;
  return `${BASE_URL}campaigns/${campaignId}/trackings${options}`;
};

export const GET_CAMPAIGN_STATS = (campaignId: number) =>
  `${BASE_URL}campaigns/${campaignId}/stats`;

export const GET_CAMPAIGN_SCHEDULER_STATS = (
  campaignId: number,
  schedulerId: number,
  startDate?: string,
  endDate?: string,
) =>
  `${BASE_URL}campaigns/${campaignId}/stats?scheduler_id=${schedulerId}${getAnalyticsDateQuery(
    startDate,
    endDate,
  )}`;

export const CREATE_RELATED_CAMPAIGN = (
  campaignId: number,
  relatedCampaignsType: string,
) =>
  `${BASE_URL}campaigns/${campaignId}/create_related_campaigns?${queryString.stringify(
    { related_campaigns_type: relatedCampaignsType },
  )}`;

export const POST_CAMPAIGN_TEST_SCHEDULER = (
  campaignId: number,
  schedulerId: number,
) => `${BASE_URL}campaigns/${campaignId}/test_scheduler/${schedulerId}`;

export const REBALANCE_ROUND_ROBIN = (campaignId: number) =>
  `${BASE_URL}campaigns/${campaignId}/rebalance_round_robin`;

export const TAGS = `${BASE_URL}tags`;
export const ASSIGN_TAGS = `${TAGS}/assign_tag`;
export const UNASSIGN_TAGS = `${TAGS}/unassign_tag`;

export const TEMPLATES = `${BASE_URL}templates`;
export const TEMPLATES_VERIFY = `${TEMPLATES}/verify_code`;
export const TEMPLATES_BY_ID = (templateId: number) =>
  `${TEMPLATES}/${templateId}`;

export const AFFILIATES_URL = `${BASE_URL}affiliates`;
export const AFFILIATE_URL = (id: number) => `${AFFILIATES_URL}/${id}`;
export const AFFILIATE_FOR_USER = `${AFFILIATES_URL}/current_user`;
export const AFFILIATE_PORTAL = `${BASE_URL.slice(
  0,
  -4,
)}affiliates/referred_companies`;

export const LIVE_CHAT_SEND_MESSAGE = `${BASE_URL}live_chat/respond`;
export const LIVE_CHAT_CONVERSATION_STATUS = `${BASE_URL}live_chat/conversation_status`;
export const LIVE_CHAT_FILTERED_CONVERSATIONS = `${BASE_URL}live_chat/filtered_conversations`;
export const LIVE_CHAT_FILTERED_CONTACTS_COUNT = `${BASE_URL}live_chat/filtered_contacts_count`;

export const START_BOTS = (botId: number, queryParams: string) =>
  `${BOTS}/${botId}/start_bots${queryParams}`;

export const CAMPAIGN_START_BOTS = (botId: number) => {
  const q = queryString.stringify(ObjectUtils.mapObjKeysToSnakeCase({ botId }));
  return `${BASE_URL}campaigns/start_bots?${q}`;
};

// Personas
export const PERSONAS = `${BASE_URL}personas`;
export const PERSONA = (personaId: number) =>
  `${BASE_URL}personas/${personaId}`;
export const PERSONAS_ME = `${PERSONAS}/me`;

export const LANDMINE_OPENS = `${BASE_URL}landmine_opens`;
// SUBSCRIPTIONS
const BILLING = `${BASE_URL}billing/`;
const BILLING_V2 = `${BASE_URL}billing/v2/`;

export const PLANS = `${BILLING}plans`;

export const SHOW_BILLING_PLAN_V2 = (planId: number) =>
  `${BILLING_V2}plans/${planId}`;

export const PLAN_INFO = `${BILLING}plans/info`;

export const FB_PAGE_FEATURES = `${BILLING}available_features`;

export const FEATURES_FOR_PLANS = `${BILLING}available_features/upgrade_list`;

export const SUBSCRIPTION = `${BILLING}subscriptions`;
export const SUBSCRIPTION_V2 = `${BILLING_V2}subscriptions`;

export const ATTACH_SMS_NUMBER = `${BILLING}subscriptions/attach_sms_number`;

export const UPDATE_SUBSCRIPTION = (subscriptionId: number) =>
  `${SUBSCRIPTION_V2}/${subscriptionId}`;

export const SUBMIT_SEATS_COUNT = (subscriptionId: number) =>
  `${BILLING}subscriptions/${subscriptionId}/update_user_seats`;

export const SUBSCRIPTION_CANCEL = (companyId: number) =>
  `${SUBSCRIPTION}/${companyId}/cancel`;

export const SUBSCRIPTION_DOWNGRADE = (
  subscriptionId: number,
  useV2Route?: boolean,
  isCancel?: boolean,
) =>
  isCancel
    ? `${SUBSCRIPTION_V2}/${subscriptionId}/cancel`
    : useV2Route
    ? `${SUBSCRIPTION_V2}/${subscriptionId}/downgrade`
    : `${SUBSCRIPTION}/${subscriptionId}/downgrade`;

export const SUBSCRIPTION_OVERAGES_ENABLED = (
  subscriptionId: number,
  useV2Route?: boolean,
) => {
  const route = useV2Route ? SUBSCRIPTION_V2 : SUBSCRIPTION;
  return `${route}/${subscriptionId}/manage_overages`;
};

export const SUBSCRIPTION_UPGRADE = (
  subscriptionId: number,
  useV2Route?: boolean,
) => {
  const route = useV2Route ? SUBSCRIPTION_V2 : SUBSCRIPTION;
  return `${route}/${subscriptionId}/upgrade`;
};

export const SUBSCRIPTION_ACTIVATE = (subscriptionId: number) =>
  `${SUBSCRIPTION}/${subscriptionId}/activate`;

export const SUBSCRIPTION_UPDATE_ALLOTMENT = (subscriptionId: number) =>
  `${SUBSCRIPTION}/${subscriptionId}/update_allotment`;

export const SUBSCRIPTION_ACCOUNT = (companyId: number) =>
  `${SUBSCRIPTION}/accounts?company_id=${companyId}`;

export const SUBSCRIPTION_COUPON = `${SUBSCRIPTION}/coupon`;

export const SUBSCRIPTION_ATTACH_PAGES = (subscriptionId: number) =>
  `${SUBSCRIPTION}/${subscriptionId}/attach_pages`;

export const SUBSCRIPTION_ADD_EXTRA_PAGES = (subscriptionId: number) =>
  `${SUBSCRIPTION}/${subscriptionId}/manage_pages`;

export const SUBSCRIPTION_MANAGE_OVERAGES = (subscriptionId: number) =>
  `${SUBSCRIPTION}/${subscriptionId}/manage_overages`;

export const SUBSCRIPTION_SWAP_PAGES = (subscriptionId: number) =>
  `${SUBSCRIPTION}/${subscriptionId}/swap_pages`;

export const SHOW_SUBSCRIPTION_V2 = (subscriptionId: number) =>
  `${SUBSCRIPTION_V2}/${subscriptionId}`;

export const CURRENT_SUBSCRIPTION = (pageId: number) =>
  `${BASE_URL}facebook_pages/${pageId}/current_subscription`;

export const TWILIO = (facebookPageId: number) =>
  `${BASE_URL}twilios/${facebookPageId}`;

export const TWILIO_ACTIVATE = (facebookPageId: number) =>
  `${BASE_URL}twilios/activate/${facebookPageId}`;

export const TWILIO_RESTORE = (facebookPageId: number) =>
  `${BASE_URL}twilios/restore/${facebookPageId}`;

export const TWILIO_AREA_CODES = (countryCode: string) =>
  `https://develop.mobilemonkey.com/area-codes/${countryCode}.json`;

export const TWILIO_GET_AVAILABLE_NUMBERS = (
  facebookPageId: number,
  countryCode: string,
  areaCode: string,
  pageSize: number,
) =>
  // tslint:disable-next-line: max-line-length
  `${BASE_URL}twilios/available_numbers?facebook_page_id=${facebookPageId}&country_code=${String(
    countryCode,
  ).trim()}&area_code=${String(areaCode).trim()}&page_size=${String(
    pageSize,
  ).trim()}`;

export const PLANS_FOR_NEXT_LEVEL = ({
  subscriptionId,
  nextLevel,
}: {
  subscriptionId: number;
  nextLevel: number;
}) =>
  `${BASE_URL}billing/subscriptions/${subscriptionId}/price_for_next_level?next_level=${nextLevel}`;

export const EXTENDED_SUBSCRIPTION = (subscriptionId: number) =>
  `${BASE_URL}billing/subscriptions/${String(subscriptionId).trim()}`;

export const SUBSCRIPTION_CANCELS = (subscriptionId: number) =>
  `${BASE_URL}billing/subscriptions/${String(subscriptionId).trim()}/cancels`;

export const DELETE_PHONE_NUMBER = (facebookPageId: number) =>
  `${BASE_URL}twilios/${facebookPageId}`;

export const DOWNGRADE_PLAN_CREDITS = (params: {
  companyId: number;
  planCode: string;
  subscriptionId: number;
}) => {
  const payload = Object.keys(params)
    .filter(key => !['companyId', 'isUpgrade'].includes(key) && params[key])
    .map(key => `${snakeCase(key)}=${encodeURIComponent(params[key])}`)
    .join('&');
  return `${BASE_URL}billing/companies/${params.companyId}/downgrade_to_plan_credits?${payload}`;
};

export const UPGRADE_PLAN_CREDITS = (params: {
  companyId: number;
  planCode: string;
  subscriptionId: number;
}) => {
  const payload = Object.keys(params)
    .filter(key => !['companyId', 'isUpgrade'].includes(key) && params[key])
    .map(key => `${snakeCase(key)}=${encodeURIComponent(params[key])}`)
    .join('&');
  return `${BASE_URL}billing/companies/${params.companyId}/upgrade_to_plan_credits?${payload}`;
};

export const CHAT_STARTERS = (params: { botId: number }) =>
  `${BASE_URL}bots/${params.botId}/chat_starters`;

export const CAMPAIGNS_OVERVIEW = (params: { botId: number }) =>
  `${BASE_URL}bots/${params.botId}/all_campaigns`;

export const ENABLED_INPUT_TRIGGERS = (params: { botId: number }) =>
  `${BASE_URL}bots/${params.botId}/all_campaigns/enabled_triggers`;

export const AUTOMATION_COUNTERS = (params: { botId: number }) =>
  `${BASE_URL}bots/${params.botId}/all_campaigns/automation_counters`;

export const GUIDES = `${BASE_URL}guides`;

export const ANALYTICS_TOP_DIALOGUES = (
  botId,
  pageNumber = 1,
  perPage = 5,
  startDate,
  endDate,
) =>
  `${BOTS}/${botId}/analytics/top_dialogues?page=${pageNumber}&per_page=${perPage}${getAnalyticsDateQuery(
    startDate,
    endDate,
    'DD-MM-YYYY',
  )}`;

export const STANDALONE_BOT = `${BASE_URL}standalone_bots`;

export const STANDALONE_BOT_ICE_BREAKER = (params: { token: string }) =>
  `${BASE_URL}standalone_bots/${params.token}/ice_breaker`;

export const STANDALONE_BOT_CONNECT_PAGE = `${BASE_URL}standalone_bots/apply_to_facebook_page`;

export const MARKETING_ANALYTICS = `${API_URL}users/ga_qualified`;

export const ANALYTICS_CONTACTS_AMOUNT = (
  botId: number,
  startDate?: string,
  endDate?: string,
) =>
  `${BOTS}/${botId}/analytic_page?granularity=day${getAnalyticsDateQuery(
    startDate,
    endDate,
  )}`;

export const UNANSWERED_QUESTIONS = (
  botId: number,
  startDate?: string,
  endDate?: string,
) =>
  `${BOTS}/${botId}/analytics/unanswered_questions${getAnalyticsDateQuery(
    startDate,
    endDate,
    'DD-MM-YYYY',
    true,
  )}`;

export const CONTACTS_BY_TYPE = (
  type: 'by_source' | 'by_platform' | 'by_platform_aggregated',
  botId: number,
  startDate?: string,
  endDate?: string,
) =>
  `${BOTS}/${botId}/analytics/contacts?tab=${type}${getAnalyticsDateQuery(
    startDate,
    endDate,
  )}`;

export const EXPORT_UNANSWERED_QUESTIONS = (botId: number) =>
  `${BOTS}/${botId}/analytics/unanswered_questions/scheduled_exports`;

export const QA_KEYWORDS = (
  botId: number,
  startDate?: string,
  endDate?: string,
) =>
  `${BOTS}/${botId}/analytics/keywords${getAnalyticsDateQuery(
    startDate,
    endDate,
    'DD-MM-YYYY',
    true,
  )}`;

export const EXPORT_QA_KEYWORDS = (botId: number) =>
  `${BOTS}/${botId}/analytics/keywords/scheduled_exports`;

export const EXPORT_OUTGOING_MESSAGES_STATS = (botId: number) =>
  `${BOTS}/${botId}/analytics/outgoing_messages/scheduled_exports`;

export const XRAY_ANALYTICS = (
  type: XRayAnalyticsType,
  botId: number,
  startDate?: string,
  endDate?: string,
) =>
  `${BOTS}/${botId}/analytics/xray/${type}${getAnalyticsDateQuery(
    startDate,
    endDate,
    'YYYY-MM-DD',
    true,
  )}`;

export const INSTAGRAM_ACCOUNTS = (fbPageId: number) =>
  `${BASE_URL}instagram_accounts/${fbPageId}`;

export const INSTAGRAM_LIST_MEDIA = (
  fbPageId: number,
  cursor: string,
  cursorValue: string,
) => {
  const paging =
    cursor && cursorValue ? `cursor=${cursor}&cursor_value=${cursorValue}` : '';
  return `${BASE_URL}instagram_accounts/list_media/${fbPageId}?${paging}`;
};

export const INSTAGRAM_MEDIA = (fbPageId: number, mediaId: string) =>
  `${BASE_URL}instagram_accounts/media/${fbPageId}/${mediaId}`;

export const ACTIVATE_INSTAGRAM = (fbPageId: number, shallow: boolean) => {
  const q = shallow ? 'shallow=true' : '';
  return `${BASE_URL}instagram_accounts/activate/${fbPageId}?${q}`;
};

export const INSTAGRAM_STORIES = (mid: string, fbPageId: number) =>
  `${BASE_URL}instagram_stories/${mid}?facebook_page_id=${fbPageId}`;

export const INSTAGRAM_REMOTE_MESSAGE = (mid: string, fbPageId: number) =>
  `${BASE_URL}instagram_messages/fetch_remote/${mid}?facebook_page_id=${fbPageId}`;

export const INSTAGRAM_LOCAL_MESSAGE = (
  mid: string,
  fbPageId: number,
  remoteContactId: string,
) =>
  `${BASE_URL}instagram_messages/fetch_local/${mid}?facebook_page_id=${fbPageId}&remote_contact_id=${remoteContactId}`;
export const DEACTIVATE_INSTAGRAM = (fbPageId: number) =>
  `${BASE_URL}instagram_accounts/deactivate/${fbPageId}`;

export const LEAD_ASSIGNMENTS = (botId: number, leadAssignmentId?: number) => {
  const leadAssignmentIdQuery = leadAssignmentId ? `/${leadAssignmentId}` : '';
  return `${BOTS}/${botId}/automation/lead_assignments${leadAssignmentIdQuery}`;
};

export const LEAD_ASSIGNMENT_GROUPS = (botId: number, groupId?: number) => {
  const groupIdQuery = groupId ? `/${groupId}` : '';
  return `${BOTS}/${botId}/automation/lead_assignment_groups${groupIdQuery}`;
};

export const EMBEDDED_CHAT_BASE = `${BASE_URL}/embedded_chat`;
export const EMBEDDED_CHAT_CONFIGURATION = (facebookPageId?: number) =>
  `${EMBEDDED_CHAT_BASE}/configuration${
    facebookPageId ? `?facebook_page_id=${facebookPageId}` : ''
  }`;
export const EMBEDDED_CHAT_AUTHORIZE = (
  facebookPageId?: number,
  llrDomain?: string,
) =>
  `${EMBEDDED_CHAT_BASE}/authorize?${new URLSearchParams(
    [
      ['facebook_page_id', facebookPageId as unknown as string],
      ['llr_domain', llrDomain as string],
    ].filter(([_k, v]) => !!v),
  ).toString()}`;

export const GOOGLE = `${BASE_URL}google`;
export const GOOGLE_OAUTH_URL = `${GOOGLE}/oauth_url`;
export const GOOGLE_REVOKE = `${GOOGLE}/revoke`;
export const GOOGLE_ACCOUNTS = `${GOOGLE}/accounts`;
export const GOOGLE_ACCESS_TOKEN = `${GOOGLE}/access_token`;
export const GOOGLE_SPREADSHEETS = `${GOOGLE}/spreadsheets`;
export const GOOGLE_SHEETS = `${GOOGLE}/sheets`;
export const GOOGLE_COLUMNS = (botId: number, webhookId: number) =>
  `${GOOGLE}/columns/${botId}/${webhookId}`;
export const AMBASSADORS_PUBLIC_URL = (ambassador: string) =>
  `${PUBLIC_API_URL}ambassadors/${ambassador}`;

export const MAILCHIMP = `${BASE_URL}mailchimp`;
export const MAILCHIMP_OAUTH_URL = `${MAILCHIMP}/oauth_url`;
export const MAILCHIMP_REVOKE = `${MAILCHIMP}/revoke`;
export const MAILCHIMP_ACCOUNTS = `${MAILCHIMP}/accounts`;
export const MAILCHIMP_LISTS = `${MAILCHIMP}/lists`;
export const MAILCHIMP_LIST_MERGE_FIELDS = `${MAILCHIMP}/list_merge_fields`;

export const SLACK = `${BASE_URL}slack`;
export const SLACK_OAUTH_URL = `${SLACK}/oauth_url`;
export const SLACK_REVOKE = `${SLACK}/revoke`;
export const SLACK_ACCOUNTS = `${SLACK}/accounts`;
export const SLACK_CHANNELS = `${SLACK}/channels`;
export const SLACK_SEND_TEST = `${SLACK}/send_test`;

export const MICROSOFT = `${BASE_URL}microsoft`;
export const MICROSOFT_OAUTH_URL = `${MICROSOFT}/oauth_url`;

export const INTEGRATIONS = `${BASE_URL}integrations`;

export const KLAVIYO = `${INTEGRATIONS}/klaviyo`;
export const KLAVIYO_LISTS = `${KLAVIYO}/lists`;
export const KLAVIYO_REVENUE = `${KLAVIYO}/revenues`;
export const KLAVIYO_OAUTH_URL = `${BASE_URL}/klaviyo/oauth_url`;
export const KLAVIYO_ACCOUNTS = `${BASE_URL}/klaviyo/accounts`;
export const KLAVIYO_REVOKE_TOKEN = `${BASE_URL}/klaviyo/revoke`;
export const KLAVIYO_API_KEYS = `${BASE_URL}/klaviyo/api_keys`;
export const KLAVIYO_API_KEY = (id: number) =>
  `${BASE_URL}/klaviyo/api_keys/${id}`;

export const SENDLANE = `${INTEGRATIONS}/sendlane`;
export const SENDLANE_LISTS = `${SENDLANE}/lists`;

export const DECLINED_CONTACTS = `${PUBLIC_API_URL}declined_contacts`;
export const CONFIRM_DECLINED_CONTACT = `${DECLINED_CONTACTS}/confirm`;

export const EMAIL_ACCOUNTS = (id?: number) =>
  `${BASE_URL}email_accounts${id ? `/${id}` : ''}`;

export const WEBSITE_TRAFFIC = `${BASE_URL}traffic_sources`;
export const WEBSITE_TRAFFIC_BY_SOURCE = (source: WebsiteTrafficSources) =>
  `${WEBSITE_TRAFFIC}/${source}`;

export const SUPPRESSION_SETTINGS = `${BASE_URL}suppression_settings`;

export const LEADS = `${BASE_URL}leads`;
export const LEADS_ATTRIBUTES = `${LEADS}/attributes`;
export const LEADS_ADDRESS_ATTRIBUTE_SEARCH = `${LEADS}/address_attribute_search`;
export const LEADS_SEARCH = `${LEADS}/search`;

export const LEADS_CONVERTATION_REQUESTS = (
  facebookPageId: number,
  status?: 'queued' | 'processed' | 'processing' | 'failed',
) => {
  const statusInQuery = status ? `?status=${status}` : '';
  return `${BASE_URL}facebook_pages/${facebookPageId}/convertation_requests${statusInQuery}`;
};

export const MY_LEADS_ATTRIBUTES = facebookPageId =>
  `${BASE_URL}facebook_pages/${facebookPageId}/my_leads/attributes`;
export const MY_LEADS_ADDRESS_ATTRIBUTE_SEARCH = facebookPageId =>
  `${BASE_URL}facebook_pages/${facebookPageId}/my_leads/address_attribute_search`;
export const MY_LEADS_SEARCH = facebookPageId =>
  `${BASE_URL}facebook_pages/${facebookPageId}/my_leads/search`;
export const MY_LEADS_CREATE_AUDIENCE = facebookPageId =>
  `${BASE_URL}facebook_pages/${facebookPageId}/my_leads/create_audience`;

export const CONTACTS_MOST_VISITED_WEB_URLS = facebookPageId =>
  `${BASE_URL}facebook_pages/${facebookPageId}/urls`;

export const LOST_LEADS_REPORT = `${BASE_URL}lost_lead_reports`;

export const SENDGRID = `${BASE_URL}sendgrid`;
export const SENDGRID_ACCOUNTS = (apiKey?: string | number) => {
  const apiKeyInQuery =
    typeof apiKey === 'number'
      ? `?api_key_id=${apiKey}`
      : typeof apiKey === 'string'
      ? `?api_key=${apiKey}`
      : '';
  return `${SENDGRID}/accounts${apiKeyInQuery}`;
};

export const SALESFORCE = `${BASE_URL}salesforce`;
export const SALESFORCE_OAUTH_URL = `${SALESFORCE}/oauth_url`;
export const SALESFORCE_ACCOUNTS = `${SALESFORCE}/accounts`;
export const SALESFORCE_REVOKE_TOKEN = `${SALESFORCE}/revoke`;

export const GEOLOCATION_DATA = 'https://geolocation-db.com/json/';

export const AI_EMAIL_TEMPLATE = facebookPageId =>
  `${BASE_URL}facebook_pages/${facebookPageId}/ai_email_template`;

export const GLOCKAPP_DELIVERABILITY_SCORE = `${BASE_URL}glockapp/deliverability`;

export const GOHIGHLEVEL = `${BASE_URL}gohighlevel`;
export const GOHIGHLEVEL_OAUTH_URL = `${GOHIGHLEVEL}/oauth_url`;
export const GOHIGHLEVEL_ACCOUNTS = `${GOHIGHLEVEL}/accounts`;
export const GOHIGHLEVEL_REVOKE_TOKEN = `${GOHIGHLEVEL}/revoke`;
export const GOHIGHLEVEL_LOCATIONS = `${INTEGRATIONS}/gohighlevel/locations`;
